import { useState, useEffect, useRef, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Bot, User, Sparkles, Loader2, Plus, ExternalLink } from "lucide-react";

const AI_MODELS = [
  { 
    id: "gpt-5.2", 
    name: "GPT-5.2", 
    description: "OpenAI's latest model",
    color: "bg-emerald-500"
  },
  { 
    id: "claude-sonnet-4.5", 
    name: "Claude Sonnet", 
    description: "Anthropic's balanced AI",
    color: "bg-purple-500"
  },
  { 
    id: "gemini-3-flash", 
    name: "Gemini 3 Flash", 
    description: "Google's fast AI",
    color: "bg-blue-500"
  }
];

const EXTERNAL_AI_AGENTS = [
  {
    id: "relapse-reset",
    name: "Relapse Reset Mind-SPA",
    description: "AI support for recovery and relapse prevention",
    url: "https://chatgpt.com/g/g-686ca8034560819183011bbf96314ed9-relapse-reset-mind-spa",
    color: "bg-red-500"
  },
  {
    id: "smaho-arts",
    name: "SMAHo.ARTS SMAHoody Live AI",
    description: "Creative arts therapy and dramatic wellness",
    url: "https://chatgpt.com/g/g-68376fd678248191a8a4f82f9b41edf4-smaho-arts-smahoody-live-ai-2",
    color: "bg-pink-500"
  },
  {
    id: "mr-rapsoul-r-live",
    name: "Mr. RAPsoul.R AI Live",
    description: "The Laughing Drama Therapist AI companion",
    url: "https://chatgpt.com/g/g-6836170ff96c8191b6a40b017745fadb-mr-rapsoul-r-ai-2-live",
    color: "bg-orange-500"
  },
  {
    id: "global-village",
    name: "SMAHOO Global Social Village",
    description: "Community forum and peer support AI",
    url: "https://chatgpt.com/g/g-6835b29e1f188191be496b26c5b06f9d-smahoo-global-social-village-forum",
    color: "bg-teal-500"
  },
  {
    id: "faith-centred",
    name: "RAPs ME.WELL Faith-Centred Mind-SPA",
    description: "Faith-based wellness and spiritual support",
    url: "https://chatgpt.com/g/g-682007e3d26c81919ca33de71dfb1910-raps-me-well-faith-centred-mind-spa-sof-b-expe",
    color: "bg-indigo-500"
  },
  {
    id: "myrtle-core",
    name: "Myrtle Core Global MH SMAHo.OS",
    description: "Global mental health operating system AI",
    url: "https://chatgpt.com/g/g-680ddcdfd9708191a02f724eb8300cf3-myrtle-core-global-mh-sof-b-smaho-os-a1-2",
    color: "bg-cyan-500"
  },
  {
    id: "rice-peas-soul",
    name: "Rice And Peas For The Soul REVISITED",
    description: "TiBTBT wellness content and guidance",
    url: "https://chatgpt.com/g/g-6801d0dca53881918059ee3f9b879927-rice-and-peas-for-the-soul-revisited-tibtbt",
    color: "bg-amber-500"
  },
  {
    id: "smahoody-ai",
    name: "Mr. RAPsoul.R SMAHoody AI",
    description: "Adaptive stress response coaching",
    url: "https://chatgpt.com/g/g-67fdc66e40948191976df748d145e5ab-mr-rapsoul-r-smahoody-ai-2",
    color: "bg-lime-500"
  }
];

const SUGGESTION_PROMPTS = [
  "How can I manage stress?", 
  "Tell me about mood tracking", 
  "What is laughter therapy?"
];

export default function AICoach() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("builtin");
  const [activeModel, setActiveModel] = useState("gpt-5.2");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const messagesEndRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await apiClient.get('/chat/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadSession = async (sid) => {
    try {
      const response = await apiClient.get(`/chat/history/${sid}`);
      setMessages(response.data);
      setSessionId(sid);
      setActiveModel(response.data[0]?.ai_model || "gpt-5.2");
    } catch (error) {
      console.error('Failed to load session:', error);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message;
    setMessage("");
    setIsLoading(true);

    setMessages(prev => [...prev, {
      message_id: `temp-${Date.now()}`,
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString()
    }]);

    try {
      const response = await apiClient.post('/chat', {
        message: userMessage,
        ai_model: activeModel,
        session_id: sessionId
      });

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, {
        message_id: `ai-${Date.now()}`,
        role: "assistant",
        content: response.data.response,
        ai_model: response.data.ai_model,
        created_at: new Date().toISOString()
      }]);

      fetchSessions();
    } catch (error) {
      // silent
      setMessages(prev => [...prev, {
        message_id: `error-${Date.now()}`,
        role: "assistant",
        content: "I apologize, but I encountered an issue. Please try again.",
        ai_model: activeModel,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const openExternalAgent = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-64px)] lg:h-screen flex flex-col" data-testid="ai-coach-page">
        {/* Main Tabs */}
        <div className="px-6 py-4 border-b border-spa-border bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Bot className="w-5 h-5 text-spa-terracotta" />
            <span className="font-medium text-spa-moss">AI Wellness Coaches</span>
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-spa-muted rounded-full p-1 h-auto">
              <TabsTrigger
                value="builtin"
                className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-white"
                data-testid="tab-builtin"
              >
                Built-in AI Coach
              </TabsTrigger>
              <TabsTrigger
                value="external"
                className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-white"
                data-testid="tab-external"
              >
                Specialized AI Agents
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tab Content */}
        {activeTab === "external" ? (
          /* External AI Agents Grid */
          <div className="flex-1 overflow-auto p-6 bg-spa-bone">
            <div className="max-w-5xl mx-auto">
              <div className="mb-6">
                <h2 className="font-serif text-2xl text-spa-moss mb-2">Specialized AI Agents</h2>
                <p className="text-spa-text-secondary">
                  Access Mr. RAPsoul.R's custom AI agents powered by ChatGPT for specialized wellness support.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {EXTERNAL_AI_AGENTS.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => openExternalAgent(agent.url)}
                    className="spa-card text-left hover:ring-2 hover:ring-spa-terracotta transition-all group"
                    data-testid={`agent-${agent.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${agent.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-serif text-lg text-spa-moss group-hover:text-spa-terracotta transition-colors">
                            {agent.name}
                          </h3>
                          <ExternalLink className="w-4 h-4 text-spa-text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-spa-text-secondary mt-1">{agent.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-8 spa-card bg-spa-sage/10">
                <div className="flex items-start gap-4">
                  <Sparkles className="w-6 h-6 text-spa-sage flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-spa-moss mb-1">About These AI Agents</h4>
                    <p className="text-sm text-spa-text-secondary">
                      These specialized AI agents are custom GPTs created by Mr. RAPsoul.R to provide focused support 
                      for different aspects of mental and emotional wellness. They open in ChatGPT and require a 
                      ChatGPT account to use.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Built-in AI Chat */
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Sidebar - Sessions History */}
            <div className="hidden lg:block w-72 border-r border-spa-border bg-white p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg text-spa-moss">Chat History</h2>
                <button 
                  onClick={startNewSession}
                  className="p-2 hover:bg-spa-muted rounded-xl transition-colors"
                  data-testid="new-chat-button"
                >
                  <Plus className="w-5 h-5 text-spa-moss" />
                </button>
              </div>
              <ScrollArea className="h-[calc(100%-60px)]">
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <button
                      key={session._id}
                      onClick={() => loadSession(session._id)}
                      className={`w-full text-left p-3 rounded-xl transition-colors ${
                        sessionId === session._id 
                          ? 'bg-spa-muted' 
                          : 'hover:bg-spa-muted/50'
                      }`}
                    >
                      <p className="text-sm font-medium text-spa-moss truncate">
                        {session.last_message?.slice(0, 30)}...
                      </p>
                      <p className="text-xs text-spa-text-secondary mt-1">
                        {session.message_count} messages
                      </p>
                    </button>
                  ))}
                  {sessions.length === 0 && (
                    <p className="text-sm text-spa-text-secondary text-center py-8">
                      No conversations yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col bg-spa-bone">
              {/* Model Selector */}
              <div className="px-6 py-3 border-b border-spa-border bg-white/80 backdrop-blur-xl">
                <Tabs value={activeModel} onValueChange={setActiveModel}>
                  <TabsList className="bg-spa-muted rounded-full p-1 h-auto">
                    {AI_MODELS.map((model) => (
                      <TabsTrigger
                        key={model.id}
                        value={model.id}
                        className="rounded-full px-4 py-2 text-xs data-[state=active]:bg-white"
                        data-testid={`model-tab-${model.id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${model.color} mr-2`}></span>
                        {model.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                  {messages.length === 0 && (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 bg-spa-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Sparkles className="w-10 h-10 text-spa-terracotta" strokeWidth={1.5} />
                      </div>
                      <h2 className="font-serif text-2xl text-spa-moss mb-3">
                        Welcome to AI Wellness Coaching
                      </h2>
                      <p className="text-spa-text-secondary max-w-md mx-auto mb-6">
                        I'm Mr. RAPsoul.R's AI companion, here to support your mental and emotional 
                        wellness journey with compassionate, trauma-informed guidance.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {SUGGESTION_PROMPTS.map((prompt) => (
                          <button
                            key={prompt}
                            onClick={() => setMessage(prompt)}
                            className="px-4 py-2 bg-white border border-spa-border rounded-full text-sm text-spa-text-secondary hover:bg-spa-muted transition-colors"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg) => (
                    <div 
                      key={msg.message_id} 
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-10 h-10 rounded-full bg-spa-sage/20 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-5 h-5 text-spa-moss" />
                        </div>
                      )}
                      <div 
                        className={`max-w-[80%] ${
                          msg.role === 'user' 
                            ? 'chat-bubble-user' 
                            : 'chat-bubble-ai'
                        }`}
                        data-testid={`message-${msg.role}`}
                      >
                        <p className="text-spa-moss whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-10 h-10 rounded-full bg-spa-terracotta flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-10 h-10 rounded-full bg-spa-sage/20 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-spa-moss" />
                      </div>
                      <div className="chat-bubble-ai">
                        <Loader2 className="w-5 h-5 animate-spin text-spa-moss" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input Area */}
              <div className="p-4 border-t border-spa-border bg-white">
                <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Share what's on your mind..."
                      className="spa-input flex-1"
                      disabled={isLoading}
                      data-testid="chat-input"
                    />
                    <Button 
                      type="submit" 
                      className="spa-button-primary px-6"
                      disabled={isLoading || !message.trim()}
                      data-testid="send-message-button"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                  <p className="text-xs text-spa-text-secondary text-center mt-2">
                    AI coaching is not a replacement for professional therapy. 
                    If you're in crisis, please seek professional help.
                  </p>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
